import React from "react";
import { ExternalLink, Loader2, Lock, XCircle, Zap } from "lucide-react";
import { MARKET_TYPES } from "../../../utils/bondPredictHelpers";
import { isOutcomeTradable } from "../../../utils/bondPredictDetailHelpers";
import { ORDER_SIDES } from "../../../utils/bondPredictTradeHelpers";
import {
  effectiveStatusFor,
  findContractLine,
  formatChancePct,
  formatCoin,
  formatPrice01,
  formatShortDate,
  getStatusMeta,
  isCancellableOrder,
} from "../../../utils/bondPredictPortfolioHelpers";

const stop = (e) => e.stopPropagation();

const ExpandShell = ({ children, variant }) => (
  <div
    className={`bp-port-item-expand${variant ? ` bp-port-item-expand-${variant}` : ""}`}
    onClick={stop}
  >
    {children}
  </div>
);

const MetaStrip = ({ items }) => (
  <div className="bp-port-expand-meta" role="group" aria-label="Details">
    {items.map((item, index) => (
      <React.Fragment key={item.key || item.label}>
        {index > 0 ? (
          <span className="bp-port-expand-sep" aria-hidden>
            ·
          </span>
        ) : null}
        <span
          className={`bp-port-expand-fact${item.accent ? ` bp-port-expand-fact-${item.accent}` : ""}`}
        >
          <span className="bp-port-expand-fact-label">{item.label}</span>
          <strong className="tabular-nums">{item.value}</strong>
        </span>
      </React.Fragment>
    ))}
  </div>
);

const ExpandToolbar = ({ children }) => (
  <div className="bp-port-expand-toolbar">{children}</div>
);

export const PortfolioHoldingExpand = ({
  position,
  userId,
  onTrade,
  onOpenMarket,
}) => {
  const market = position?.market || {};
  const marketId = market?._id || position?.market;
  const type = market?.type || MARKET_TYPES.BINARY;
  const isMulti = type === MARKET_TYPES.MULTI_BINARY;
  const contractLine = isMulti ? findContractLine(market, position?.outcome?.selectionKey) : null;
  const effectiveStatus = effectiveStatusFor(market, contractLine);
  const statusMeta = getStatusMeta(effectiveStatus);
  const shares = Number(position?.shares || 0);
  const invested = Number(position?.invested || 0);
  const avgEntry = Number(position?.avgEntryPrice || 0);
  const tradable = isOutcomeTradable(market, position?.outcome);
  const canSell = tradable && shares > 0;
  const lastActivity = position?.lastTradeAt;

  const metaItems = [
    {
      label: "Shares",
      value: shares.toFixed(1),
    },
    {
      label: "Invested",
      value: formatCoin(invested),
    },
    {
      label: "Avg entry",
      value: `${formatPrice01(avgEntry)} (${formatChancePct(avgEntry)})`,
    },
  ];

  if (lastActivity) {
    metaItems.push({
      label: "Last activity",
      value: formatShortDate(lastActivity),
    });
  }
  if (isMulti && contractLine?.timings?.closeAt) {
    metaItems.push({
      label: "Closes",
      value: formatShortDate(contractLine.timings.closeAt),
    });
  }
  if (isMulti && contractLine?.timings?.resolveBy) {
    metaItems.push({
      label: "Result by",
      value: formatShortDate(contractLine.timings.resolveBy),
    });
  }
  if (market?.category) {
    metaItems.push({
      label: "Category",
      value: String(market.category).replace(/_/g, " "),
    });
  }

  return (
    <ExpandShell variant="holding">
      <div className="bp-port-expand-compact">
        <MetaStrip items={metaItems} />
        <ExpandToolbar>
          {tradable ? (
            <>
              <button
                type="button"
                className="bp-port-trade-btn bp-port-trade-btn-buy"
                onClick={() => onTrade?.(ORDER_SIDES.BUY)}
                disabled={!userId}
              >
                <Zap size={11} aria-hidden /> Buy
              </button>
              <button
                type="button"
                className="bp-port-trade-btn bp-port-trade-btn-sell"
                onClick={() => onTrade?.(ORDER_SIDES.SELL)}
                disabled={!userId || !canSell}
              >
                <Zap size={11} aria-hidden /> Sell
              </button>
            </>
          ) : (
            <span className="bp-port-expand-closed">
              <Lock size={10} aria-hidden /> {statusMeta.label}
            </span>
          )}
          <button
            type="button"
            className="bp-port-open-market-btn"
            onClick={() => marketId && onOpenMarket?.(marketId)}
          >
            <ExternalLink size={11} aria-hidden /> Market
          </button>
        </ExpandToolbar>
      </div>
    </ExpandShell>
  );
};

export const PortfolioOrderExpand = ({
  order,
  userId,
  onTrade,
  onOpenMarket,
  onCancelOrder,
  cancelBusy,
}) => {
  const market = order?.market || {};
  const marketId = market?._id || order?.market;
  const type = market?.type || MARKET_TYPES.BINARY;
  const contractLine =
    type === MARKET_TYPES.MULTI_BINARY
      ? findContractLine(market, order?.outcome?.selectionKey)
      : null;
  const typeLabel = String(order?.type || "LIMIT").toUpperCase();
  const quantity = Number(order?.quantity || 0);
  const filled = Number(order?.filledQuantity || 0);
  const remaining = Math.max(0, quantity - filled);
  const price = typeof order?.price === "number" ? order.price : null;
  const notional = price != null ? price * quantity : null;
  const canCancel = isCancellableOrder(order, market, contractLine);
  const tradable = isOutcomeTradable(market, order?.outcome);
  const isMarketOrder = typeLabel === "MARKET";

  const metaItems = [
    {
      label: "Filled",
      value: `${filled.toFixed(1)} / ${quantity.toFixed(1)}`,
    },
    {
      label: "Remaining",
      value: remaining.toFixed(1),
    },
    {
      label: "Notional",
      value: notional != null ? formatCoin(notional) : "—",
    },
    {
      label: "Placed",
      value: formatShortDate(order?.createdAt),
    },
  ];

  if (!isMarketOrder && price != null) {
    metaItems.splice(2, 0, {
      label: "Limit",
      value: `${formatPrice01(price)} (${formatChancePct(price)})`,
    });
  }

  return (
    <ExpandShell variant="order">
      <div className="bp-port-expand-compact">
        <MetaStrip items={metaItems} />
        <ExpandToolbar>
          {tradable ? (
            <>
              <button
                type="button"
                className="bp-port-trade-btn bp-port-trade-btn-buy"
                onClick={() => onTrade?.(ORDER_SIDES.BUY)}
                disabled={!userId}
              >
                <Zap size={11} aria-hidden /> Buy
              </button>
              <button
                type="button"
                className="bp-port-trade-btn bp-port-trade-btn-sell"
                onClick={() => onTrade?.(ORDER_SIDES.SELL)}
                disabled={!userId}
              >
                <Zap size={11} aria-hidden /> Sell
              </button>
            </>
          ) : (
            <span className="bp-port-expand-closed">
              <Lock size={10} aria-hidden /> Closed
            </span>
          )}
          <button
            type="button"
            className="bp-port-open-market-btn"
            onClick={() => marketId && onOpenMarket?.(marketId)}
            disabled={cancelBusy}
          >
            <ExternalLink size={11} aria-hidden /> Market
          </button>
          {canCancel ? (
            <button
              type="button"
              className="bp-port-cancel-btn"
              onClick={() => onCancelOrder?.(order)}
              disabled={cancelBusy}
            >
              {cancelBusy ? (
                <Loader2 size={11} className="bp-spin" aria-hidden />
              ) : (
                <XCircle size={11} aria-hidden />
              )}
              Cancel
            </button>
          ) : null}
        </ExpandToolbar>
      </div>
    </ExpandShell>
  );
};
